import { useAtom } from 'jotai';
import { useAtomValue } from 'jotai/utils';
import { useEffect, useRef } from 'react';
import { serviceToken } from 'src/api/functions/accessApi';
import { pickerModeAtom } from 'src/shared/pickerMode';
import { securitySettingsAtom } from 'src/shared/settings/security';
import { useAccessToken } from './useAccessToken';

const providers = ['local', 'weburl', 'flickr', 'box', 'onedrive', 'googledrive'];

const fileSpinEvents = ['close', 'ready', 'complete', 'start'];

type FileSpinHookOptions = {
  events?: {
    close?: () => void;
    ready?: () => void;
    start?: () => void;
    complete?: (data: any) => void;
    open?: () => void;
  };
};

const useFileSpinPicker = (options?: FileSpinHookOptions) => {
  const [mode, setMode] = useAtom(pickerModeAtom);

  const accessToken = useAccessToken();
  const securitySettings = useAtomValue(securitySettingsAtom);

  // Latest callbacks, read by the stable wrappers subscribed below. Without
  // this indirection every render would push a fresh handler onto the
  // widget's global event bus (the old effect depended on `options`, which
  // is a new object each render), so `complete` fired once per render.
  const eventsRef = useRef(options?.events);
  eventsRef.current = options?.events;

  if (
    securitySettings?.dropbox_app_key?.length > 0 &&
    securitySettings?.dropbox_app_secret?.length > 0 &&
    !providers.includes('dropbox')
  ) {
    providers.push('dropbox');
  }

  const showPicker = () => {
    window.FileSpin.showPicker();
    options?.events?.open?.();
  };
  const resetPicker = () => window.FileSpin.resetPicker();
  const hidePicker = () => window.FileSpin.hidePicker();

  const replaceContent = async (id: string) => {
    const serviceKey = await serviceToken(accessToken);

    window.FileSpin.initPicker(
      {
        uploadKey: securitySettings.upload_key,
        providers,
        serviceToken: serviceKey,
        fileToReplaceId: id,
        autoUpload: false,
        display: false,
        // hideAfterUpload: true,
      },
      () => {
        showPicker();
        if (!mode || mode === 'add') {
          setMode('replace');
        }
      },
    );
  };

  const uploadContent = () => {
    if (!mode || mode === 'replace') {
      window.FileSpin.initPicker(
        {
          uploadKey: securitySettings.upload_key,
          providers,
          autoUpload: false,
          minimizeOnUpload: false,
          display: false,
        },
        () => {
          showPicker();
          setMode('add');
        },
      );
    } else {
      resetPicker();
      showPicker();
    }
  };

  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout>;
    const wrappers: Array<[string, (data: any) => void]> = [];

    // The loader script is injected async by FileSpinPickerProvider, so
    // window.FileSpin may not exist yet on first mount — retry until it does.
    const subscribe = () => {
      if (cancelled) return;
      if (!window.FileSpin) {
        retryTimer = setTimeout(subscribe, 200);
        return;
      }
      fileSpinEvents.forEach((event) => {
        const wrapper = (data: any) => {
          eventsRef.current?.[event as keyof typeof eventsRef.current]?.(data);
        };
        window.FileSpin.on(event, wrapper);
        wrappers.push([event, wrapper]);
      });
    };

    subscribe();

    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
      wrappers.forEach(([event, wrapper]) => window.FileSpin?.off?.(event, wrapper));
    };
  }, []);

  return {
    showPicker,
    hidePicker,
    replaceContent,
    uploadContent,
  };
};

export default useFileSpinPicker;
